/**
 * linkify — split a plain-text string into text / link segments so a chat
 * message body can render clickable URLs.
 *
 * Recognises explicit http(s):// URLs, `www.`-prefixed hosts, and bare
 * domains with a known TLD (`google.com`) — schemeless matches get an
 * `https://` href so they open correctly. Kept side-effect-free (no React /
 * RN / Expo imports) so it's trivially unit-testable in the node/vitest suite.
 * The chat screen consumes `parseLinks` and renders each segment (link
 * segments get an onPress that opens the URL in a new tab on web / externally
 * on native).
 */

export type LinkSegment =
  | { type: 'text'; value: string }
  | { type: 'link'; value: string; href: string }

// Bare domains (`google.com`) are linkified too, but ONLY when the last label
// is a recognised TLD. Gating on an allowlist is what keeps prose from turning
// into links: `config.ts`, `e.g.`, `v0.9.282`, and `warcarp.community` all have
// a final label that isn't in this set, so none of them match. Common gTLDs +
// the country codes we're likely to see; extend as needed.
const TLDS = [
  'com', 'org', 'net', 'edu', 'gov', 'mil', 'int',
  'io', 'co', 'ai', 'app', 'dev', 'xyz', 'info', 'biz', 'me', 'tv', 'gg',
  'online', 'site', 'tech', 'store', 'blog', 'news', 'live', 'shop', 'games',
  'us', 'uk', 'ca', 'de', 'fr', 'jp', 'au', 'nl', 'eu', 'in', 'it', 'es',
  'ch', 'se', 'no', 'nz', 'ru', 'cn', 'br', 'za', 'ie', 'be', 'at', 'pl',
]

// Three alternatives, most-specific first:
//   1. an explicit http(s):// URL — runs to the next whitespace / angle bracket
//   2. a `www.`-prefixed host — same greedy run
//   3. a bare domain: one-or-more dot-separated labels ending in a known TLD,
//      with an optional path/query/fragment
// Trailing punctuation is peeled back off below so a URL at the end of a
// sentence doesn't swallow the period / paren.
const URL_RE = new RegExp(
  '(https?:\\/\\/[^\\s<]+' +
    '|www\\.[^\\s<]+' +
    '|\\b(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\\.)+(?:' +
    TLDS.join('|') +
    ')\\b(?:[/?#][^\\s<]*)?)',
  'gi',
)

// Punctuation that commonly sits AFTER a URL in prose and should not be part of
// the link (a closing paren is only peeled when the URL has no matching open
// paren — so `https://en.wikipedia.org/wiki/Foo_(bar)` keeps its paren).
const TRAILING_PUNCT = /[.,;:!?'"]+$/

/**
 * Split `text` into an ordered list of text / link segments.
 *
 * A string with no URLs returns a single text segment (or an empty array for
 * empty input). Callers render text segments as plain strings and link
 * segments as pressable text using `href` (which prepends `https://` to
 * `www.`-style matches so they open correctly).
 */
export function parseLinks(text: string): LinkSegment[] {
  const out: LinkSegment[] = []
  let lastIndex = 0
  let m: RegExpExecArray | null
  URL_RE.lastIndex = 0
  while ((m = URL_RE.exec(text)) !== null) {
    const full = m[0]
    const hasScheme = /^https?:\/\//i.test(full)

    if (m.index > lastIndex) {
      out.push({ type: 'text', value: text.slice(lastIndex, m.index) })
    }

    // Don't linkify the host of an email address (`user@google.com`): a
    // schemeless match immediately preceded by '@' is the domain part of an
    // email, not a standalone link. Emit it as plain text instead.
    if (!hasScheme && text[m.index - 1] === '@') {
      out.push({ type: 'text', value: full })
      lastIndex = m.index + full.length
      continue
    }

    let url = full
    let trailing = ''

    // Peel a trailing unbalanced ')' first (e.g. "(see https://x.com)"), then
    // any generic trailing punctuation, moving it into a following text segment.
    if (url.endsWith(')') && !url.includes('(')) {
      trailing = ')' + trailing
      url = url.slice(0, -1)
    }
    const punct = TRAILING_PUNCT.exec(url)
    if (punct) {
      trailing = punct[0] + trailing
      url = url.slice(0, url.length - punct[0].length)
    }

    if (url.length === 0) {
      // The whole match was punctuation we peeled — emit it as text.
      out.push({ type: 'text', value: full })
    } else {
      // Schemeless matches (www.foo.com, google.com) need an explicit scheme
      // to open correctly; the display text keeps the original form.
      const href = /^https?:\/\//i.test(url) ? url : `https://${url}`
      out.push({ type: 'link', value: url, href })
      if (trailing) out.push({ type: 'text', value: trailing })
    }

    lastIndex = m.index + full.length
  }
  if (lastIndex < text.length) {
    out.push({ type: 'text', value: text.slice(lastIndex) })
  }
  return out
}

/** True iff `text` contains at least one linkifiable URL. */
export function hasLink(text: string): boolean {
  URL_RE.lastIndex = 0
  return URL_RE.test(text)
}
